import React, { useState } from 'react'
import { Box, Chip, Link, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

type KycPerson = {
  _id: string
  name: string
  documentNumber: string
  relationships?: Array<{
    type: string
    name?: string
    documentNumber?: string
    endDate?: string
    representedPerson?: {
      name: string
      documentNumber: string
    }
  }>
}

interface ColumnKYCProps {
  items?: KycPerson[]
  type: 'shareholders' | 'relatedPersons'
  companyDocumentNumber?: string
}

const ColumnKYC: React.FC<ColumnKYCProps> = ({ items, type, companyDocumentNumber }) => {
  const { t } = useTranslation(['common', 'messages'])
  const [showMore, setShowMore] = useState(false)
  const { currentCountry } = useSelector((state: { kybCountry: { currentCountry: string } }) => state.kybCountry)

  if (!Array.isArray(items) || items.length === 0) return <Typography variant="body2">---</Typography>

  const getRelationshipInfo = (person: KycPerson) => {
    if (type === 'relatedPersons') {
       const relationship = person.relationships?.find((rel) => rel.documentNumber === companyDocumentNumber)
      return {
        functionType: relationship?.type || '---',
        representedPerson: relationship?.representedPerson?.name || '---',
        endDate: relationship?.endDate ? new Date(relationship.endDate).toLocaleDateString() : '---',
      }
    }
    return null
  }

  const renderPerson = (person: KycPerson) => {
    const relationshipInfo = getRelationshipInfo(person)

    if (type === 'shareholders') {
      const displayDocumentNumber = person.documentNumber?.includes('kyb.documentNumber.notFound') 
        ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
        : person.documentNumber
      return (
        <Typography
          variant="caption"
          sx={{ fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <Link
            href={`/kyc/${currentCountry}/detail/${person._id}`}
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontWeight: 'bold', color: '#19db55' }}
          >
            CPF: ({displayDocumentNumber})
          </Link>
          {' | '}
          {person.name}
        </Typography>
      )
    }

    // relatedPersons
    const relInfo = relationshipInfo as { functionType: string; representedPerson: string; endDate: string }
    const represented = relInfo?.representedPerson || '---'
    const funcType = relInfo?.functionType || '---'
    const endDate = relInfo?.endDate || '---'
    const displayDocumentNumber = person.documentNumber?.includes('kyb.documentNumber.notFound') 
      ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
      : person.documentNumber

    return (
      <Typography
        variant="caption"
        sx={{ fontWeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={`Función: ${funcType} | Representado: ${represented} | Fin: ${endDate}`}
      >
        <Link
          href={`/kyc/${currentCountry}/detail/${person._id}`}
          target="_blank"
          rel="noopener"
          underline="hover"
          sx={{ fontWeight: 'bold', color: '#19db55' }}
        >
          CPF: ({displayDocumentNumber})
        </Link>
        {' | '}
        {person.name}
        {' | '}
        {`${t('common:roleType')}: ${t(`common:${funcType}`)}`}
        {' | '}
        {`${t('common:expirationDate')}: ${endDate}`}
      </Typography>
    )
  }

  const displayItems = showMore ? items : items.slice(0, 3)

  const toggleShowMore = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowMore((prev) => !prev)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 1,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxHeight: showMore ? '220px' : 'none',
          overflowY: showMore ? 'auto' : 'visible',
          overflowX: 'auto',
          transition: 'max-height 0.2s',
          pr: 1,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', minWidth: 0, width: '100%' }}>
          {displayItems.map((p) => (
            <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, minWidth: 0, width: '100%' }}>
              {renderPerson(p)}
            </Box>
          ))}
        </Box>
      </Box>
      {items.length > 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1, mb: 1 }}>
          <Chip
            label={showMore ? 'Show Less' : 'Show More'}
            onClick={toggleShowMore}
            size="small"
            sx={{
              backgroundColor: '#19DB55',
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#19DB55',
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default ColumnKYC
